/* [대시보드] 오늘 요약 — 로그인하자마자 "지금 챙길 것"을 숫자로 보여 준다.

   왜 만들었나 (11-02, 원장 지시 — 모바일 UX 점검)
   예전 대시보드는 왼쪽 메뉴를 큰 카드로 다시 그린 것뿐이라, 휴대폰에서 화면 7.5장 길이에
   정보는 하나도 없었다. 첫 화면에서 오늘 상황이 보이고, 누르면 해당 화면으로 가게 한다.

   읽기 비용
   교직원은 users·classes·enrollments·학원 달력을 로그인 때 이미 받아 둔다(DataContext).
   여기서 따로 읽는 것은 '오늘 날짜' 문서뿐이다 — 출결 기록·상담·업무 요청은 하루 치,
   학교 시험 일정은 아직 끝나지 않은 것만, 통화 회신은 열린 것 최대 50건.

   ⚠ 출결 숫자는 통합 출결 관리(AttendanceManager 의 radarData)와 같은 기준으로 센다.
     그 화면의 집계 규칙(시즌 반만, 시험 기간 면제, 콜타임 지나면 미등원)을 바꾸면 여기도 함께 바꾼다.
     두 화면의 숫자가 다르면 사용자는 둘 다 믿지 않는다.

   [캠퍼스 — docs/campus §2-4]
   교직원 숫자는 지금 고른 캠퍼스(칩) 것만 센다 — 출결 화면이 캠퍼스로 거른 반(campusClasses)만 보므로 같은 기준.
   상담·가입 대기·업무 요청·통화 회신도 같은 칩을 따른다. 표시 없는 옛 기록은 컷오프 전이면 목동이라 지금 숫자는 그대로다. */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot, limit } from 'firebase/firestore';
import { UserCheck, UserPlus, Phone, ClipboardList, CalendarClock, ChevronRight } from 'lucide-react';
import { db } from '../firebase';
import { APP_ID } from '../constants';
import { useData } from '../contexts/DataContext';
import { toDateStr, getEntriesOn } from '../utils/academyCalendar';
import { pickSeasonForToday } from '../hooks/useSeasonAutoSelect';
import { isSameSchool } from '../utils/schoolName';

const BASE = `artifacts/${APP_ID}/public/data`;
const DAYS = ['일', '월', '화', '수', '목', '금', '토'];

const hhmm = (d) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

/** 오늘 출결 집계 — AttendanceManager radarData 와 같은 규칙 (순수 함수) */
export const summarizeAttendance = ({ todayStr, dayStr, nowHHMM, enrollments, users, classes, seasons, logs, examCalendars, lecturerId }) => {
    const seasonId = pickSeasonForToday(seasons, 'legacy');
    const seasonClassIds = new Set(
        (classes || []).filter(c => (seasonId === 'legacy' ? !c.season : c.season === seasonId)).map(c => c.id)
    );
    const userById = new Map((users || []).map(u => [u.id, u]));
    const logByStudent = new Map((logs || []).map(l => [l.studentId, l]));

    // 출결 화면처럼 수강(반) 단위로 센다 — 오늘 두 반을 듣는 학생은 두 번 잡힌다.
    let attended = 0, late = 0, waiting = 0, examLeave = 0;
    (enrollments || []).forEach(enroll => {
        if (enroll.status !== 'active' || !seasonClassIds.has(enroll.classId)) return;
        if (lecturerId && enroll.lecturerId !== lecturerId) return;
        const sch = enroll.schedules?.find(s => s.dayOfWeek === dayStr);
        if (!sch) return;
        const student = userById.get(enroll.studentId);
        if (!student) return;

        const exempt = (examCalendars || []).some(cal =>
            cal.isAttendanceExempt && isSameSchool(cal.schoolName, student.schoolName)
            && todayStr >= cal.startDate && todayStr <= cal.endDate);
        if (exempt) { examLeave++; return; }

        const log = logByStudent.get(enroll.studentId);
        if (log) { if (log.status !== 'absent') attended++; return; }
        // 등원 시각이 빈 수강은 출결 화면도 지각으로 치지 않는다.
        if (sch.callTime && nowHHMM > sch.callTime) late++; else waiting++;
    });
    return { total: attended + late + waiting, attended, late, waiting, examLeave };
};

/** Firestore 실시간 구독 하나를 상태로. enabled 가 false 면 구독하지 않는다. */
const useLiveDocs = (enabled, makeQuery, deps) => {
    const [state, setState] = useState({ docs: [], loading: enabled, error: false });
    useEffect(() => {
        if (!enabled) { setState({ docs: [], loading: false, error: false }); return undefined; }
        setState(s => ({ ...s, loading: true }));
        return onSnapshot(makeQuery(),
            snap => setState({ docs: snap.docs.map(d => ({ id: d.id, ...d.data() })), loading: false, error: false }),
            err => { console.error('[TodaySummary]', err); setState({ docs: [], loading: false, error: true }); });
    }, [enabled, ...deps]);
    return state;
};

/* 숫자 칸 하나. tone 은 완성된 클래스 이름으로 둔다 — Tailwind 는 조립한 이름을 못 찾는다. */
const TONE = {
    alert: 'border-rose-200 bg-rose-50 text-rose-700',
    warn: 'border-amber-200 bg-amber-50 text-amber-800',
    calm: 'border-slate-200 bg-white text-slate-800',
};
const Tile = ({ icon: Icon, label, value, unit, sub, tone = 'calm', onClick, wide }) => (
    <button
        type="button"
        onClick={onClick}
        className={`${wide ? 'col-span-2' : ''} ${TONE[tone]} border rounded-2xl p-3.5 text-left flex flex-col gap-1 transition hover:shadow-md active:scale-[0.99] focus:outline-none focus:ring-2 focus:ring-indigo-500`}
    >
        <span className="flex items-center gap-1.5 text-xs font-bold opacity-80">
            <Icon size={14} className="shrink-0" />{label}
            <ChevronRight size={14} className="ml-auto opacity-50 shrink-0" />
        </span>
        <span className="text-2xl font-black leading-tight tabular-nums">
            {value}{unit && <span className="text-sm font-bold ml-0.5">{unit}</span>}
        </span>
        {sub && <span className="text-xs font-bold opacity-70 truncate">{sub}</span>}
    </button>
);

/* 학생·학부모 — 오늘 수업 (11-02 학생·학부모 점검: 학생 대시보드에는 '오늘'이 하나도 없었다).
   My Imperial Day(StudentDashboard)와 같은 기준: 재원 중인 수강의 '반 시간표'에서 오늘 요일.
   학부모는 DataContext 가 자녀 수강만 내려 주므로 같은 계산에 자녀 이름만 붙인다. 추가로 읽는 문서는 없다. */
const FamilyToday = ({ currentUser, navigate, users, classes, enrollments, academyCalendar, todayStr, dayStr }) => {
    const isParent = currentUser.role === 'parent';
    const ownerIds = isParent ? (currentUser.linkedChildrenIds || []) : [currentUser.id];
    const items = [];
    (enrollments || []).forEach(e => {
        if (e.status !== 'active' || !ownerIds.includes(e.studentId)) return;
        const cls = (classes || []).find(c => c.id === e.classId);
        const sch = cls?.schedules?.find(s => s.dayOfWeek === dayStr);
        if (!sch) return;
        const child = isParent ? (users || []).find(u => u.id === e.studentId) : null;
        items.push({ id: e.id, name: cls.name || e.className || '정규 수업', start: sch.startTime || '', end: sch.endTime || '', room: sch.room || '', child: child?.name });
    });
    items.sort((a, b) => String(a.start).localeCompare(String(b.start)));
    const closed = getEntriesOn(todayStr, academyCalendar).find(c => c.isClosed === true);

    return (
        <section aria-label="오늘 수업" className="bg-white border border-slate-200 rounded-2xl p-4">
            <div className="flex items-center mb-2">
                <h2 className="text-sm font-black text-slate-800 flex items-center gap-1.5"><CalendarClock size={15} className="text-indigo-500" /> 오늘 수업</h2>
                {!isParent && (
                    <button type="button" onClick={() => navigate('/my-day')} className="ml-auto text-xs font-bold text-indigo-600 px-2 py-1.5 rounded-lg hover:bg-indigo-50 flex items-center">
                        My Imperial Day <ChevronRight size={14} />
                    </button>
                )}
            </div>
            {closed ? (
                <p className="text-sm font-bold text-slate-500">오늘은 휴원일입니다{closed.title ? ` (${closed.title})` : ''}.</p>
            ) : items.length === 0 ? (
                <p className="text-sm font-bold text-slate-400">오늘은 수업이 없습니다.</p>
            ) : (
                <ul className="divide-y divide-slate-100">
                    {items.map(it => (
                        <li key={it.id} className="py-2 flex items-baseline gap-3">
                            <span className="text-sm font-black text-slate-800 tabular-nums shrink-0">{it.start}{it.end ? `~${it.end}` : ''}</span>
                            <span className="text-sm font-bold text-slate-700 min-w-0 break-keep">
                                {it.child && <span className="text-indigo-600 mr-1">{it.child}</span>}{it.name}
                                {it.room && <span className="text-slate-400 font-medium"> · {it.room}</span>}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
};

const TodaySummary = ({ currentUser }) => {
    const navigate = useNavigate();
    const { users, classes, enrollments, masterData, academyCalendar, campusClasses, inCampus, userInCampus } = useData() || {};
    const here = (d) => (inCampus ? inCampus(d) : true);
    const role = currentUser?.role;

    // 콜타임이 지났는지가 시간에 따라 바뀌므로 1분마다 다시 센다.
    const [now, setNow] = useState(() => new Date());
    useEffect(() => { const t = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(t); }, []);
    const todayStr = toDateStr(now);
    const nowHHMM = hhmm(now);

    const showAttendance = ['admin', 'admin_assistant', 'lecturer'].includes(role);
    const showConsult = ['admin', 'admin_assistant', 'lecturer'].includes(role);
    const showPending = ['admin', 'admin_assistant'].includes(role);
    const showFollowups = role === 'admin';
    const showRequests = ['admin', 'admin_assistant', 'lecturer', 'ta'].includes(role);

    const logs = useLiveDocs(showAttendance,
        () => query(collection(db, `${BASE}/attendance_logs`), where('date', '==', todayStr)), [todayStr]);
    // 시험 면제 판정용. 아직 끝나지 않은 시험만 받는다(출결 화면은 전체를 받는다).
    const exams = useLiveDocs(showAttendance,
        () => query(collection(db, `${BASE}/academic_calendars`), where('endDate', '>=', todayStr)), [todayStr]);
    const consults = useLiveDocs(showConsult,
        () => query(collection(db, `${BASE}/consultations`), where('date', '==', todayStr)), [todayStr]);
    const followups = useLiveDocs(showFollowups,
        () => query(collection(db, `${BASE}/call_followups`), where('status', '==', 'open'), limit(50)), []);
    const requests = useLiveDocs(showRequests,
        () => query(collection(db, `${BASE}/daily_requests`), where('targetDate', '==', todayStr)), [todayStr]);

    const closedEntry = useMemo(
        () => getEntriesOn(todayStr, academyCalendar).find(c => c.isClosed === true) || null,
        [todayStr, academyCalendar]);

    const att = useMemo(() => {
        if (!showAttendance) return null;
        return summarizeAttendance({
            todayStr, dayStr: DAYS[now.getDay()], nowHHMM,
            enrollments, users, classes: campusClasses || classes, seasons: masterData?.seasons,
            logs: logs.docs, examCalendars: exams.docs,
            lecturerId: role === 'lecturer' ? currentUser.id : null,
        });
    }, [showAttendance, todayStr, nowHHMM, enrollments, users, classes, campusClasses, masterData, logs.docs, exams.docs, role, currentUser?.id]);

    const consultInfo = useMemo(() => {
        const list = consults.docs.filter(c => (inCampus ? inCampus(c) : true))
            .filter(c => c.status === 'scheduled' || c.status === 'completed' || c.status === 'attending');
        const next = list.filter(c => c.status === 'scheduled' && String(c.time || '') >= nowHHMM)
            .sort((a, b) => String(a.time).localeCompare(String(b.time)))[0];
        return { count: list.length, next };
    }, [consults.docs, nowHHMM, inCampus]);

    // 가입 대기는 소속을 모르면(가입 화면이 아직 캠퍼스를 묻지 않음) 모든 캠퍼스에 센다 — userInCampus 규칙
    const pendingCount = useMemo(() => (users || []).filter(u => u.status === 'pending' && (userInCampus ? userInCampus(u) : true)).length, [users, userInCampus]);
    const campusRequests = requests.docs.filter(here);
    const openRequests = campusRequests.filter(r => r.status !== 'completed').length;
    const campusFollowups = followups.docs.filter(here);

    const tiles = [];
    if (showAttendance) {
        const loading = logs.loading || exams.loading;
        if (closedEntry) {
            tiles.push(<Tile key="att" wide icon={UserCheck} label="오늘 출결" value="휴원일" sub={closedEntry.title} onClick={() => navigate('/attendance')} />);
        } else if (att && att.total === 0 && !loading) {
            tiles.push(<Tile key="att" wide icon={UserCheck} label={role === 'lecturer' ? '내 수업 출결' : '오늘 출결'} value="수업 없음" sub={att.examLeave ? `시험 기간 면제 ${att.examLeave}명` : '오늘 요일에 잡힌 수업이 없습니다'} onClick={() => navigate('/attendance')} />);
        } else {
            const subParts = [];
            if (att?.late) subParts.push(`미등원 ${att.late}명`);
            if (att?.waiting) subParts.push(`등원 전 ${att.waiting}명`);
            if (att?.examLeave) subParts.push(`시험 면제 ${att.examLeave}명`);
            tiles.push(<Tile key="att" wide icon={UserCheck}
                label={role === 'lecturer' ? '내 수업 출결' : '오늘 출결'}
                value={loading || logs.error ? '–' : `${att.attended} / ${att.total}`} unit={loading || logs.error ? '' : '명 등원'}
                sub={logs.error ? '출결 기록을 불러오지 못했습니다' : subParts.join(' · ')}
                tone={att?.late ? 'alert' : 'calm'}
                onClick={() => navigate('/attendance')} />);
        }
    }
    if (showConsult) {
        const n = consultInfo.next;
        tiles.push(<Tile key="consult" icon={CalendarClock} label="오늘 상담"
            value={consults.loading ? '–' : consultInfo.count} unit={consults.loading ? '' : '건'}
            sub={n ? `다음 ${n.time} ${n.studentName || ''}` : (consultInfo.count ? '남은 상담 없음' : '')}
            onClick={() => navigate('/consult')} />);
    }
    if (showPending) {
        tiles.push(<Tile key="pending" icon={UserPlus} label="가입 승인 대기"
            value={pendingCount} unit="명" tone={pendingCount ? 'warn' : 'calm'}
            onClick={() => navigate('/users')} />);
    }
    if (showFollowups) {
        const n = campusFollowups.length;
        tiles.push(<Tile key="followups" icon={Phone} label="회신할 통화"
            value={followups.loading ? '–' : (n >= 50 ? '50+' : n)} unit={followups.loading ? '' : '건'}
            tone={n ? 'warn' : 'calm'} onClick={() => navigate('/clinic-tasks')} />);
    }
    if (showRequests) {
        tiles.push(<Tile key="requests" icon={ClipboardList} label="오늘 업무 요청"
            value={requests.loading ? '–' : openRequests} unit={requests.loading ? '' : '건 남음'}
            sub={campusRequests.length ? `전체 ${campusRequests.length}건` : ''}
            tone={openRequests ? 'warn' : 'calm'} onClick={() => navigate('/clinic-tasks')} />);
    }

    if (role === 'student' || role === 'parent') {
        return <FamilyToday currentUser={currentUser} navigate={navigate} users={users} classes={classes}
            enrollments={enrollments} academyCalendar={academyCalendar} todayStr={todayStr} dayStr={DAYS[now.getDay()]} />;
    }
    if (tiles.length === 0) return null;
    return (
        <section aria-label="오늘 요약" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5">
            {tiles}
        </section>
    );
};

export default TodaySummary;
